import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Search } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { useEffect, useState } from "react";

import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

import { useEsAdmin } from "../datos/useEsAdmin";
import type { Negocio } from "../dominio/tipos";
import { abrirBuscador, cerrarBuscador, useBuscadorAbierto } from "./buscador";
import { EtiquetaEtapa } from "./EtiquetaEtapa";
import { useAbrirFicha } from "./useAbrirFicha";

/**
 * Buscar cualquier ficha (02/10/2026): Leads solo enseña los pendientes y el Pipeline las
 * que están en marcha; para todo lo demás (aparcadas, «no volver a contactar», cobradas…)
 * está el buscador. Busca por nombre, persona de contacto, teléfono y dirección, y abre la
 * ficha en la ventana. Ctrl+K o ⌘K en escritorio.
 */
export const BuscadorFichas = () => {
  const translate = useTranslate();
  const abierto = useBuscadorAbierto();
  const abrirFicha = useAbrirFicha();
  const [texto, setTexto] = useState("");
  const [consulta, setConsulta] = useState("");
  // S10b: los descubiertos de Prospect (sin entregar ni dueño) no salen al buscar; Jan y
  // Robert los incluyen con esta casilla. Un setter nunca los ve (candado).
  const esAdmin = useEsAdmin();
  const [conDescubiertos, setConDescubiertos] = useState(false);

  // Atajo de teclado y espera de 250 ms para no consultar con cada letra.
  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        abrirBuscador();
      }
    };
    window.addEventListener("keydown", alPulsar);
    return () => window.removeEventListener("keydown", alPulsar);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setConsulta(texto.trim()), 250);
    return () => clearTimeout(t);
  }, [texto]);
  useEffect(() => {
    if (!abierto) setTexto("");
  }, [abierto]);

  const { data, isFetching } = useGetList<Negocio>(
    "negocios",
    {
      pagination: { page: 1, perPage: 20 },
      sort: { field: "updated_at", order: "DESC" },
      filter:
        esAdmin && conDescubiertos
          ? { q: consulta }
          : { q: consulta, en_leads: true },
    },
    { enabled: abierto && consulta.length >= 2 },
  );

  return (
    <DialogPrimitive.Root
      open={abierto}
      onOpenChange={(a) => (a ? abrirBuscador() : cerrarBuscador())}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-[10vh] z-50 w-[min(94vw,40rem)] -translate-x-1/2 overflow-hidden rounded-xl border bg-background shadow-lg"
        >
          <DialogPrimitive.Title className="sr-only">
            {translate("nevora.buscador.titulo")}
          </DialogPrimitive.Title>
          <Command shouldFilter={false}>
            <CommandInput
              value={texto}
              onValueChange={setTexto}
              placeholder={translate("nevora.buscador.placeholder")}
              aria-label={translate("nevora.buscador.titulo")}
            />
            {esAdmin ? (
              <label className="flex items-center gap-2 border-b px-3 py-2 text-xs text-muted-foreground">
                <Checkbox
                  checked={conDescubiertos}
                  onCheckedChange={(v) => setConDescubiertos(v === true)}
                  data-testid="buscar-descubiertos"
                />
                {translate("nevora.buscador.con_descubiertos")}
              </label>
            ) : null}
            <CommandList className="max-h-[60vh]">
              {consulta.length >= 2 && !isFetching ? (
                <CommandEmpty>
                  {translate("nevora.buscador.vacio")}
                </CommandEmpty>
              ) : null}
              {consulta.length < 2 ? (
                <p className="px-4 py-6 text-sm text-muted-foreground">
                  {translate("nevora.buscador.ayuda")}
                </p>
              ) : null}
              {consulta.length >= 2
                ? (data ?? []).map((n) => (
                    <CommandItem
                      key={n.id}
                      value={String(n.id)}
                      onSelect={() => {
                        cerrarBuscador();
                        abrirFicha(n.id);
                      }}
                      className="flex items-center gap-3"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">
                          {n.nombre}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {[n.persona_contacto, n.telefono, n.sector]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                      <EtiquetaEtapa etapa={n.etapa} />
                    </CommandItem>
                  ))
                : null}
            </CommandList>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

/** Botón «Buscar» para las cabeceras móviles. */
export const BotonBuscar = () => {
  const translate = useTranslate();
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={abrirBuscador}
      aria-label={translate("nevora.buscador.titulo")}
    >
      <Search className="size-5" />
    </Button>
  );
};
